import React from 'react';
import S from 'common/service/sanctuary';
import Textfield from 'common/mdc/textfield';
import { func, bool } from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import {
  EMPTY,
  NOT_EMPTY,
  WITHIN,
  translateOperator,
} from './operator';

import SearchConstraint, { constraintShape } from './constraint';

const messages = defineMessages({
  within: {
    id: 'search.advanced.criteria.constraint.within',
    defaultMessage: 'within {range}m of {latitude}, {longitude}',
  },
  range: {
    id: 'search.advanced.criteria.constraint.range',
    defaultMessage: '{input} m',
  },
  of: {
    id: 'search.advanced.criteria.constraint.of',
    defaultMessage: 'of',
  },
  lat: {
    id: 'search.advanced.criteria.constraint.lat',
    defaultMessage: '{input} lat',
  },
  lon: {
    id: 'search.advanced.criteria.constraint.lon',
    defaultMessage: '{input} lon',
  },
});

export const translateConstraint = constraint => S.pipe([
  S.get(S.is(String), 'operator'),
  S.chain(operator => S.toMaybe(({
    [EMPTY]: translateOperator(constraint.operator),
    [NOT_EMPTY]: translateOperator(constraint.operator),
    [WITHIN]: constraint.value
      ? <FormattedMessage
        values={{
          range: constraint.value[0],
          latitude: constraint.value[1],
          longitude: constraint.value[2],
        }}
        {...messages.within}
      />
      : null,
  })[operator])),
])(constraint);

const getContent = (constraint, props) => {
  const value = constraint.value || S.map(S.K(0), Array(3).fill());

  const onChangeBetween = (newValue, position) => {
    value[position] = newValue;
    return props.onChange(value.map(Number));
  };

  return ({
    [EMPTY]: null,
    [NOT_EMPTY]: null,
    [WITHIN]: (
      <div className="criteria-location">
        <FormattedMessage
          values={{
            input: <Textfield
              {...props}
              inline
              type="number"
              onChange={e => onChangeBetween(e.target.value, 0)}
              value={value[0]}
            />,
          }}
          {...messages.range}
        />
        <div className="criteria-location-form">
          <div className="constraint-label">
            <FormattedMessage {...messages.of} />
          </div>
          <FormattedMessage
            values={{
              input: <Textfield
                {...props}
                inline
                type="number"
                onChange={e => onChangeBetween(e.target.value, 1)}
                value={value[1]}
              />,
            }}
            {...messages.lat}
          />
          <FormattedMessage
            values={{
              input: <Textfield
                {...props}
                inline
                type="number"
                onChange={e => onChangeBetween(e.target.value, 2)}
                value={value[2]}
              />,
            }}
            {...messages.lon}
          />
        </div>
      </div>
    ),
  })[constraint.operator];
};

const Location = ({
  nullable,
  constraint,
  onValueChange,
  ...props
}) => (
  <SearchConstraint
    {...props}
    constraint={constraint}
    operators={[
      nullable && EMPTY,
      nullable && NOT_EMPTY,
      WITHIN,
    ].filter(Boolean)}
  >{getContent(constraint, {
    className: 'criteria-input',
    onChange: e => onValueChange(S.is(Array, e) ? e : [e.target.value]),
    value: (constraint.value && constraint.value[0]) || '',
    onKeyDown: (e) => {
      e.stopPropagation();
    },
  })}
  </SearchConstraint>
);

Location.propTypes = {
  nullable: bool,
  constraint: constraintShape.isRequired,
  onValueChange: func.isRequired,
};

Location.defaultProps = {
  nullable: false,
};

export default Location;
